import type { RefObject } from "react";
import type { PaperLine } from "../types";
import { MarkdownLine } from "./MarkdownLine";

export const TableBlock = ({
  lines,
  highlightedNums,
  firstHighlightNum,
  firstHighlightRef,
}: {
  lines: PaperLine[];
  highlightedNums?: Set<number>;
  firstHighlightNum?: number;
  firstHighlightRef?: RefObject<HTMLDivElement | null>;
}) => {
  const anyHighlighted = highlightedNums
    ? lines.some((l) => highlightedNums.has(l.num))
    : false;
  const isFirst = firstHighlightNum != null
    && lines.some((l) => l.num === firstHighlightNum);

  const highlightClasses = anyHighlighted
    ? "bg-amber-50 border-l-[3px] border-l-amber-400 pl-4 -ml-4 rounded-r"
    : "";

  const markdown = lines.map((l) => l.text.trim()).join("\n");

  return (
    <div
      id={`L${lines[0].num}`}
      ref={isFirst ? firstHighlightRef : undefined}
      className={`overflow-x-auto my-3 ${highlightClasses}`}
    >
      <MarkdownLine content={markdown} />
    </div>
  );
};

const splitExtractedRow = (line: string): string[] => {
  // Patent ingestion uses " | " as a cell separator rather than Markdown
  // delimiters. Leading/trailing empty cells are therefore meaningful.
  return line.split("|").map((cell) => cell.trim());
};

type ExtractedRow = {
  cells: string[];
  spanning: boolean;
};

type ExtractedTable = {
  rows: ExtractedRow[];
  footnotes: string[];
  unreliable: boolean;
};

const reconstructExtractedRows = (lines: string[]): ExtractedTable => {
  const rows: ExtractedRow[] = [];
  const footnotes: string[] = [];
  const pipeLineColumnCounts = lines
    .filter((line) => line.includes("|"))
    .map((line) => splitExtractedRow(line).length);
  const columnCount = Math.max(
    1,
    ...pipeLineColumnCounts,
  );
  let inTable = false;
  let hasFullWidthRow = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line.includes("|")) {
      const cells = splitExtractedRow(line);
      const previous = rows[rows.length - 1];
      inTable = true;

      if (
        hasFullWidthRow && cells.length < columnCount
        && previous
        && !previous.spanning
        && previous.cells.length === columnCount
      ) {
        cells.forEach((cell, index) => {
          if (cell) {
            previous.cells[index] = `${previous.cells[index] || ""} ${cell}`.trim();
          }
        });
      } else if (!hasFullWidthRow && cells.length < columnCount) {
        rows.push({
          cells: [cells.filter(Boolean).join(" · ")],
          spanning: true,
        });
      } else {
        rows.push({
          cells,
          spanning: false,
        });
        if (cells.length === columnCount) hasFullWidthRow = true;
      }
      continue;
    }

    if (inTable) {
      footnotes.push(line);
    } else {
      rows.push({ cells: [line], spanning: true });
    }
  }

  const matchingColumnCount = pipeLineColumnCounts.filter(
    (count) => count === columnCount,
  ).length;
  const unreliable = columnCount > 12
    || (pipeLineColumnCounts.length > 1
      && matchingColumnCount / pipeLineColumnCounts.length < 0.4);

  return { rows, footnotes, unreliable };
};

export const ExtractedTableBlock = ({ lines }: { lines: string[] }) => {
  const { rows, footnotes, unreliable } = reconstructExtractedRows(lines);
  const columnCount = Math.max(1, ...rows.map((row) => row.cells.length));
  const firstGridRow = rows.findIndex((row) => !row.spanning);
  const firstDataRow = rows.findIndex((row, index) => {
    if (index < firstGridRow || row.spanning) return false;
    const numericCells = row.cells.filter((cell) =>
      /^(?:\d+(?:\.\d+)?|[-—/])$/.test(cell.trim())
    );
    return numericCells.length >= 2;
  });
  const hasStructuredBody = firstGridRow >= 0 && firstDataRow > firstGridRow;
  const labels = hasStructuredBody ? rows.slice(0, firstGridRow) : [];
  const headerRows = hasStructuredBody
    ? rows.slice(firstGridRow, firstDataRow)
    : [];
  const bodyRows = hasStructuredBody ? rows.slice(firstDataRow) : rows;
  const headers = Array.from({ length: columnCount }, (_, columnIndex) => {
    const parts = headerRows
      .map((row) => row.cells[columnIndex]?.trim())
      .filter((part): part is string => Boolean(part));
    return [...new Set(parts)].join(" ");
  });

  if (rows.length === 0) return null;

  if (unreliable) {
    return (
      <div
        className="mt-3 overflow-x-auto rounded-md border border-zinc-200 bg-zinc-50"
        role="region"
        aria-label="Extracted table"
        tabIndex={0}
      >
        <pre className="min-w-max whitespace-pre-wrap p-4 font-mono text-xs leading-6 text-zinc-800">
          {lines.join("\n")}
        </pre>
      </div>
    );
  }

  return (
    <div
      className="mt-3 overflow-x-auto rounded-md border border-zinc-200 shadow-sm"
      role="region"
      aria-label="Extracted table"
      tabIndex={0}
    >
      {labels.length > 0 && (
        <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-3">
          {labels.map((row, index) => (
            <p
              key={`${index}-${row.cells[0]}`}
              className={index === 0
                ? "text-sm font-semibold text-zinc-900"
                : "mt-1 text-xs leading-5 text-zinc-600"}
            >
              {row.cells[0]}
            </p>
          ))}
        </div>
      )}
      <table className="w-full min-w-[42rem] border-collapse text-sm">
        {hasStructuredBody && (
          <thead className="bg-zinc-100">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  scope="col"
                  className="border-b border-r border-zinc-300 px-3 py-2.5 text-left font-semibold leading-5 text-zinc-800 align-bottom last:border-r-0"
                >
                  {header || "\u00a0"}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, rowIndex) => {
            const isSpanningLabel = row.spanning && columnCount > 1;
            return (
              <tr
                key={`${rowIndex}-${row.cells.join("|")}`}
                className="border-b border-zinc-200 even:bg-zinc-50/70 last:border-b-0"
              >
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    colSpan={isSpanningLabel ? columnCount : 1}
                    className={`border-r border-zinc-200 px-3 py-2.5 leading-5 text-zinc-800 align-top last:border-r-0 ${isSpanningLabel ? "bg-zinc-50 font-medium" : ""
                      }`}
                  >
                    {cell || "\u00a0"}
                  </td>
                ))}
                {!isSpanningLabel && row.cells.length < columnCount && (
                  <td
                    colSpan={columnCount - row.cells.length}
                    className="border-r border-zinc-200 px-3 py-2.5 last:border-r-0"
                  />
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footnotes.length > 0 && (
        <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-3">
          {footnotes.map((footnote, index) => (
            <p
              key={`${index}-${footnote}`}
              className="text-2xs leading-5 text-zinc-600 [&+&]:mt-1"
            >
              {footnote}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
